import { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import { Card, Stack, Chip, Divider, CircularProgress } from "@mui/material";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiProgress from "components/VuiProgress";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import LineChart from "examples/Charts/LineCharts/LineChart";
import BarChart from "examples/Charts/BarCharts/BarChart";

import linearGradient from "assets/theme/functions/linearGradient";
import colors from "assets/theme/base/colors";

import SatisfactionRate from "layouts/dashboard/components/SatisfactionRate";
import ReferralTracking from "layouts/dashboard/components/ReferralTracking";
import OrdersOverview from "layouts/dashboard/components/OrderOverview";

import roomKeyDashboardDummy from "layouts/dashboard/data/roomKeyDashboardDummy";

import {
  IoLogIn,
  IoLogOut,
  IoMoon,
  IoBed,
  IoClipboard,
  IoPeople,
  IoStatsChart,
} from "react-icons/io5";
import {
  FaClipboardList,
  FaCrown,
  FaHouseUser,
  FaMoon,
  FaPercentage,
  FaPlaneArrival,
  FaPlaneDeparture,
  FaUserCheck,
} from "react-icons/fa";
import { MdOutlineBedroomChild } from "react-icons/md";
import GuestLifestyleInsights from "./components/GuestLifestyleInsights";
import DashboardPropertyHeader from "./components/DashboardHotelFilter";
import useCommon from "hooks/useCommon";
import { getDashboardCounters } from "store/slice/dashboardSlice";
import { getDashboardOccupancy } from "store/slice/dashboardSlice";
import TodayOverview from "./components/TodayOverview";

function Dashboard() {
  const { DISPATCH, STORE } = useCommon();
  const { black, light, primary, dark } = colors;

  const {
    counters,
    occupancy,
    countersLoading,
    occupancyLoading,
  } = STORE((state) => state.dashboard);
  const selectedHotelId = STORE((state) => state.ui.hotelId);

  const selectedHotels = STORE((state) => state.ui.hotels);

  const selectedHotel = useMemo(() => {
    return selectedHotels?.find((h) => h._id === selectedHotelId);
  }, [selectedHotels, selectedHotelId]);

  const [occupancyFilter, setOccupancyFilter] = useState("monthly");

  const dashboard = roomKeyDashboardDummy?.data;
  const { gradients } = colors;
  const { cardContent } = gradients;

  // const occupancyTrend = dashboard.occupancyTrends[occupancyFilter];

  const occupancyChartData = useMemo(() => {
    return [
      {
        name: "Occupancy %",
        data: occupancy?.chart?.map((item) => Math.round(item?.occupancyPct || 0)) || [],
      },
    ];
  }, [occupancy]);

  const occupancyChartOptions = useMemo(() => {
    return {
      chart: {
        fontFamily: "Montserrat, sans-serif",
        foreColor: "#535b55",
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      stroke: {
        curve: "smooth",
        width: 1.5,
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: "dark",
          type: "vertical",
          opacityFrom: 0.28,
          opacityTo: 0.05,
        },
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        axisBorder: { show: true, color: "#bcbfba" },
        axisTicks: { show: true, color: "#bcbfba" },
        categories: occupancy?.chart?.map((item) => item?.label) || [],
        labels: {
          hideOverlappingLabels: true,
          style: {
            colors: dark.main,
            fontSize: "12px",
          },
        },
      },
      yaxis: {
        min: 0,
        max: Math.max(...(occupancy?.chart?.map((i) => i.occupancyPct || 0) || [100])) + 10,
        labels: {
          formatter: (val) => `${Math.round(val)}%`,
          style: {
            colors: dark.main,
          },
        },
      },
      legend: {
        show: true,
        labels: {
          colors: dark.main,
        },
      },
      grid: {
        borderColor: "#d8dad5",
        strokeDashArray: 4,
        padding: {
          top: 10,
          bottom: 0,
        },
      },
      tooltip: {
        theme: "light",
        y: {
          formatter: (val) => `${Math.round(val)}%`,
        },
      },
      colors: ["#2e6954"],
      responsive: [
        {
          breakpoint: 600,
          options: {
            xaxis: {
              tickAmount: 4,
              labels: {
                rotate: 0,
                hideOverlappingLabels: true,
                trim: true,
                style: { fontSize: "10px" },
              },
            },
            yaxis: {
              labels: { style: { fontSize: "10px" } },
            },
          },
        },
      ],
    };
  }, [occupancy]);

  const guestLifestyleData = {
    completionRate: 81,
    completedProfiles: 52,
    totalEligible: 64,

    temperature: {
      average: 69,
      min: 65,
      max: 78,
    },

    culinary: [
      { label: "Healthy Path", value: 42 },
      { label: "Indulgent Escape", value: 34 },
      { label: "Protein-Focused", value: 24 },
    ],

    morning: [
      { label: "Espresso", value: 46 },
      { label: "Stretch", value: 31 },
      { label: "Workout", value: 23 },
    ],

    evening: [
      { label: "Wine & Dine", value: 44 },
      { label: "In-Room Cinema", value: 33 },
      { label: "Social Happenings", value: 23 },
    ],

    neighborhood: [
      { label: "Hidden Gems", icon: "", value: 58 },
      { label: "Social Pulse", icon: "", value: 42 },
    ],

    concierge: [
      { label: "Independent Stay", value: 61 },
      { label: "High Touch Concierge", value: 39 },
    ],
  };

  useEffect(() => {
    if (selectedHotelId) {
      DISPATCH(
        getDashboardCounters({
          hotelId: selectedHotelId,
        })
      );
    }
  }, [DISPATCH, selectedHotelId]);

  useEffect(() => {
    if (selectedHotelId) {
      DISPATCH(
        getDashboardOccupancy({
          hotelId: selectedHotelId,
          period: occupancyFilter,
        })
      );
    }
  }, [DISPATCH, selectedHotelId, occupancyFilter]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <VuiBox py={1} sx={{
        "& .MuiCard-root": {
          background: "#faf9f7",
          border: "1px solid #bfc2bc",
          borderRadius: "20px",
          boxShadow: "none",
          backdropFilter: "none",
        },
      }}>
        {/* HOTEL SELECTOR + KPI CARDS  */}
        <VuiBox mb={2}>
          <DashboardPropertyHeader dashboard={dashboard} />
        </VuiBox>

        {/* OCCUPANCY TRENDS + GUEST INSIGHTS */}
        <VuiBox mb={2}>
          <Grid container spacing="18px">
            <Grid item xs={12} lg={8}>
              <Card sx={{ height: "100%", p: 1.5 }}>
                <VuiBox>
                  <VuiBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={0}
                    sx={{ flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}
                  >
                    <VuiBox>
                      <VuiTypography
                        variant="lg"
                        fontWeight="bold"
                        mb="4px"
                        sx={{ display: "block", lineHeight: 1.4, fontSize: "16px", fontWeight: 600 }}
                      >
                        Occupancy Trends
                      </VuiTypography>

                      <VuiTypography
                        variant="button"
                        fontWeight="regular"
                        sx={{
                          display: "none",
                          fontSize: "12px",
                          "@media (min-width: 768px)": { display: "block" },
                        }}
                      >
                        Occupancy percentage trends over time
                      </VuiTypography>
                    </VuiBox>

                    <Stack direction="row" spacing={1}>
                      {["today", "weekly", "monthly", "yearly"].map((filter) => (
                        <Chip
                          key={filter}
                          label={filter}
                          aria-label={`${filter} occupancy trends`}
                          aria-pressed={occupancyFilter === filter}
                          clickable
                          onClick={() => setOccupancyFilter(filter)}
                          sx={{
                            height: "32px",
                            borderRadius: "3px",
                            fontSize: "10px",
                            textTransform: "capitalize",
                            background:
                              occupancyFilter === filter ? "#e9ede8" : "transparent",
                            color: dark.main,
                            border: "1px solid #929c94",
                            "&:hover": { background: "#e9ede8" },
                          }}
                        />
                      ))}
                    </Stack>
                  </VuiBox>

                  <VuiBox sx={{ height: "330px", minWidth: 0 }}>
                    {occupancyFilter === "today" ? (
                      <VuiBox
                        height="100%"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <VuiBox sx={{ position: "relative", display: "inline-flex" }}>
                          <CircularProgress
                            variant="determinate"
                            value={Math.round(occupancy?.current?.occupancyPct || 0)}
                            size={200}
                            thickness={2.2}
                            color="success"
                            sx={{
                              "& .MuiCircularProgress-circle": {
                                strokeLinecap: "round",
                              },
                            }}
                          />

                          <VuiBox
                            sx={{
                              inset: 0,
                              position: "absolute",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <VuiBox textAlign="center">
                              <VuiTypography cvariant="button" fontWeight="regular">
                                Current
                              </VuiTypography>

                              <VuiTypography
                                variant="h1"
                                fontWeight="medium"
                                lineHeight={1}
                                sx={{
                                  fontSize: "62px",
                                }}
                              >
                                {Math.round(occupancy?.current?.occupancyPct || 0)}%
                              </VuiTypography>

                              <VuiTypography variant="button" fontWeight="regular">
                                Occupancy
                              </VuiTypography>
                            </VuiBox>
                          </VuiBox>
                        </VuiBox>

                        <VuiTypography variant="caption" mt={0}>
                          {occupancy?.current?.occupied || 0} occupied rooms •{" "}
                          {occupancy?.current?.sellableRooms || 0} available rooms
                        </VuiTypography>
                      </VuiBox>
                    ) : !occupancyLoading && occupancy?.chart?.length > 0 ? (
                      <LineChart
                        key={occupancyFilter}
                        lineChartData={occupancyChartData}
                        lineChartOptions={occupancyChartOptions}
                      />
                    ) : (
                      <VuiBox
                        height="100%"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        gap={1.5}
                      >
                        <CircularProgress
                          size={24}
                          thickness={5}
                          sx={{
                            color: primary.main,
                          }}
                        />

                        <VuiTypography variant="caption">Loading occupancy trends...</VuiTypography>
                      </VuiBox>
                    )}
                  </VuiBox>
                </VuiBox>
              </Card>
            </Grid>

            <Grid item xs={12} lg={4}>
              <ReferralTracking
                title="Guest Insights"
                leftCards={[
                  {
                    label: "Returning Guests Today",
                    value: `${Math.round(counters?.guestInsights?.todayReturningGuestPct || 0)}%`,
                    icon: <FaUserCheck size="13px" color="#22c55e" />,
                    fullWidth: true,
                    progress: Math.round(counters?.guestInsights?.todayReturningGuestPct || 0),
                  },
                  {
                    label: "Top Guest",
                    value: counters?.guestInsights?.topGuest?.name
                      ? `${counters?.guestInsights?.topGuest?.name} • ${counters?.guestInsights?.topGuest?.stayCount} stays`
                      : "-",
                    icon: <FaCrown size="13px" color="#fbbf24" />,
                    fullWidth: true,
                    progress: Math.min(
                      (counters?.guestInsights?.topGuest?.stayCount || 0) * 10,
                      100
                    ),
                    route: counters?.guestInsights?.topGuest?.guestId
                      ? `/guestLibrary/profile-history/${counters?.guestInsights?.topGuest?.guestId}`
                      : null,
                  },
                  {
                    label: "Total Guests",
                    value: (counters?.guestInsights?.totalGuests || 0).toLocaleString(),
                    icon: <IoPeople size="14px" color="#3b82f6" />,
                    progress: Math.min(counters?.guestInsights?.totalGuests || 0, 100),
                  },
                  {
                    label: "Avg Stay",
                    value: `${counters?.guestInsights?.avgStayNights || 0} nights`,
                    icon: <FaMoon size="13px" color="#10b981" />,
                    progress: Math.min((counters?.guestInsights?.avgStayNights || 0) * 20, 100),
                  },
                ]}
                scoreLabel="Returning"
                scoreValue={`${Math.round(counters?.guestInsights?.returningGuestPct || 0)}%`}
                scoreSubLabel="Guest Ratio"
                progressValue={Math.round(counters?.guestInsights?.returningGuestPct || 0)}
              />
            </Grid>
            {/* <Grid item xs={12} lg={6} xl={4}>
              <SatisfactionRate
                title="Occupancy"
                subtitle={`${dashboard.occupancy.occupiedRooms}/${dashboard.occupancy.totalRooms} rooms occupied`}
                value={dashboard.occupancy.percentage}
                centerIcon={<MdOutlineBedroomChild size="22px" color="#fff" />}
                centerLabel={`${dashboard.occupancy.percentage}%`}
                bottomLabel={`${dashboard.occupancy.availableRooms} rooms available`}
                minLabel="0%"
                maxLabel="100%"
              />
            </Grid>

            <Grid item xs={12} lg={6} xl={4}>
              <SatisfactionRate
                title="Returning Guests"
                subtitle={`${dashboard.returningGuests.total} returning guests today`}
                value={Math.round(dashboard.returningGuests.percentageOfArrivals)}
                centerIcon={<FaUserCheck size="22px" color="#fff" />}
                centerLabel={`${Math.round(dashboard.returningGuests.percentageOfArrivals)}%`}
                bottomLabel="Of today's arrivals"
                minLabel="0%"
                maxLabel="100%"
              />
            </Grid> */}

            {/* <Grid item xs={12} lg={12} xl={4}>
              <SatisfactionRate
                title="Preference Completion Ratio"
                subtitle={`${dashboard.preferenceCompletion.completedForms}/${dashboard.preferenceCompletion.totalEligibleForms} completed`}
                value={Math.round(dashboard.preferenceCompletion.completionPercentage)}
                centerIcon={<FaPercentage size="20px" color="#fff" />}
                centerLabel={`${Math.round(dashboard.preferenceCompletion.completionPercentage)}%`}
                bottomLabel={`${dashboard.preferenceCompletion.incompleteForms} incomplete`}
                minLabel="0%"
                maxLabel="100%"
              />
            </Grid>  */}
          </Grid>
        </VuiBox>

        <VuiBox mb={2}>
          <TodayOverview key={selectedHotelId} />
        </VuiBox>

        {/* PREFERENCE INSIGHTS + OPERATIONS SNAPSHOT */}
        <VuiBox mb={2}>
          <Grid p={0} item xs={12} lg={12}>
            <GuestLifestyleInsights />
          </Grid>
        </VuiBox>

        {/* OPERATIONS TIMELINE */}
        {/* <Grid container spacing={3}>
          <Grid item xs={12}>
            <OrdersOverview
              title="Today's Overview"
              growthText="+18%"
              growthSubText="more activity than yesterday"
              items={dashboard.operationsTimeline}
            />
          </Grid>
        </Grid> */}
      </VuiBox>
    </DashboardLayout>
  );
}

export default Dashboard;
